import get from 'lodash/get'
import pick from 'lodash/pick'
import { CONFIG_KEYS, getClientConfigBool } from '../config'
import { getOccurrences } from './occurrences'
import { getEntryLink } from './get-entry-link'
import getImageFromEntry, {
  MASTER,
  STANDARD,
  SQUARE,
} from './get-image-from-entry'
import getMasterVideoFromEntry from './get-master-video-from-entry'
import { getStoreTitle } from './get-store-title'
import { getSponsoredTopic } from './get-sponsored-topic'
import { callToAction } from './call-to-action'
import getDescriptiveLocation from './get-descriptive-location'

export const MAX_NEWS_ENTRIES = 6

export function moduleForDisplayMode(centre, moduleData) {
  const contentTypeId = get(moduleData, 'sys.contentTypeId')

  switch (contentTypeId) {
    case 'introductions': {
      const module = {
        ...pick(moduleData, ['sys.id']),
        displayMode: 'Feature',
        title: get(moduleData, 'entry.title'),
        body: get(moduleData, 'entry.body') || '',
        entryType: getSponsoredTopic(
          get(moduleData, 'entry'),
          get(moduleData, 'entry.sys.entryType')
        ),
        videoUrl: getMasterVideoFromEntry(moduleData.entry).src,
        desktopImageUrl: getImageFromEntry(moduleData.entry, MASTER).src,
        mobileImageUrl: getImageFromEntry(moduleData.entry, STANDARD).src,
        analyticsMetadata: get(moduleData, 'analyticsMetadata'),
        entryAnalyticsMetadata: get(moduleData, 'entry.analyticsMetadata'),
      }
      if (moduleData.entry.sys.entryType === 'featuredLink') {
        module.link = getEntryLink(centre.slug, moduleData.entry, {
          supportsAbsolute: true,
        })
      }
      return module
    }
    case 'utilityPanel':
      return {
        ...pick(moduleData, ['sys.id', 'sys.contentTypeId']),
        displayMode: 'UtilityPanel',
        heading: get(moduleData, 'heading'),
        entries: get(moduleData, 'entries'),
        centre,
      }

    case 'section':
      return pick(moduleData, [
        'sys.id',
        'sys.contentTypeId',
        'displayMode',
        'title',
        'analyticsMetadata',
      ])

    case 'promotion':
      return {
        ...pick(moduleData, [
          'sys.id',
          'sys.contentTypeId',
          'image',
          'title',
          'heading',
          'shortDescription',
          'callToActionLabel',
          'callToActionAddress',
          'analyticsMetadata',
        ]),
        displayMode: 'Promotion',
      }

    case 'socialFeed': {
      const entries = pick(moduleData, ['socialFeedData']).socialFeedData
        .entries
      return {
        ...pick(moduleData, ['sys.id', 'sys.contentTypeId']),
        title: moduleData.title,
        stackShortName: moduleData['socialFeedData'].stackShortName,
        displayMode: 'SocialFeed',
        entries,
      }
    }

    case 'centreReviews': {
      const reviewData = pick(moduleData, ['reviewData']).reviewData

      return {
        ...pick(moduleData, ['sys.id', 'sys.contentTypeId', 'title']),
        ...pick(reviewData, ['rating', 'count', 'entries']),
        displayMode: 'CentreReviews',
      }
    }

    case 'basicContent': {
      return pick(moduleData, [
        'sys.id',
        'sys.contentTypeId',
        'title',
        'bodyCopy',
        'callsToAction',
        'analyticsMetadata',
      ])
    }

    case getClientConfigBool(CONFIG_KEYS.NEXT_PUBLIC_FEAT_FLAG_CONTENT_BLOCK) &&
      'contentBlock': {
      return pick(moduleData, [
        'sys.id',
        'sys.contentTypeId',
        'title',
        'bodyCopy',
      ])
    }

    case 'dynamicContent': {
      return pick(moduleData, ['sys.contentTypeId'])
    }

    case 'automatedCollection':
    case 'manualCollection':
    default: {
      const entries = moduleData.entries
      const entry = moduleData.entries[0]
      const collectionProps = pick(moduleData, [
        'sys.id',
        'sys.contentTypeId',
        'displayMode',
        'relatedContent',
        'contentTypes',
        'analyticsMetadata',
      ])

      if (
        [
          'Feature',
          'Full Width (With Image)',
          'Full Width (Text Only)',
        ].includes(collectionProps.displayMode)
      ) {
        const imageProps = {}
        if (collectionProps.displayMode === 'Feature') {
          imageProps.desktopImageUrl = getImageFromEntry(entry, MASTER).src
          imageProps.mobileImageUrl = getImageFromEntry(entry, STANDARD).src
          imageProps.evolvedImage = getImageFromEntry(entry, SQUARE).src
        } else {
          imageProps.imageUrl = getImageFromEntry(entry, STANDARD).src
        }
        return {
          ...collectionProps,
          title: entry.title,
          topic: getSponsoredTopic(entry),
          body: entry.shortDescription,
          ...imageProps,
          videoUrl: getMasterVideoFromEntry(entry).src,
          link: entry.url || getEntryLink(centre.slug, entry),
          location: getDescriptiveLocation({
            level: entry.level,
            physicalAddress: entry.physicalAddress,
            hosted: entry.hosted,
            storeTitle: getStoreTitle(centre, entry),
          }),
          occurrences: getOccurrences(true, entry, centre.timeZone),
          entryAnalyticsMetadata: entry.analyticsMetadata,
        }
      }

      if (['Spotlight'].includes(collectionProps.displayMode)) {
        return {
          ...collectionProps,
          heading: moduleData.title,
          callToAction: callToAction(
            collectionProps.relatedContent,
            centre.slug,
            get(moduleData, 'entries[0].sys.contentTypeId', {}),
            get(moduleData, 'templatePath')
          ),
          entries: entries.map(e => ({
            id: e.sys.id,
            contentTypeId: e.sys.contentTypeId,
            link: e.url || getEntryLink(centre.slug, e),
            desktopImageUrl: getImageFromEntry(e, STANDARD).src,
            mobileImageUrl: getImageFromEntry(e, SQUARE).src,
            title: e.title,
            location: getDescriptiveLocation({
              level: e.level,
              physicalAddress: e.physicalAddress,
              hosted: e.hosted,
              storeTitle: getStoreTitle(centre, e),
            }),
            occurrences: getOccurrences(true, e, centre.timeZone),
            analyticsMetadata: e.analyticsMetadata,
          })),
        }
      }

      if (['News'].includes(collectionProps.displayMode)) {
        return {
          ...collectionProps,
          heading: moduleData.title,
          callToAction: callToAction(
            collectionProps.relatedContent,
            centre.slug
          ),
          entries: entries
            .map(e => ({
              id: e.sys.id,
              link: getEntryLink(centre.slug, e),
              desktopImageUrl: getImageFromEntry(e, STANDARD).src,
              mobileImageUrl: getImageFromEntry(e, SQUARE).src,
              title: e.title,
              location: getDescriptiveLocation({
                level: e.level,
                physicalAddress: e.physicalAddress,
                hosted: e.hosted,
                storeTitle: getStoreTitle(centre, e),
              }),
              occurrences: getOccurrences(true, e, centre.timeZone),
              analyticsMetadata: e.analyticsMetadata,
            }))
            .filter((e, i) => i < MAX_NEWS_ENTRIES),
        }
      }

      if (['Flip Flop'].includes(collectionProps.displayMode)) {
        return {
          ...collectionProps,
          body: entry.shortDescription,
          heading: entry.title,
          imageUrl: getImageFromEntry(entry, STANDARD).src,
          superheading: getSponsoredTopic(entry),
          link: entry.url || getEntryLink(centre.slug, entry),
          location: getDescriptiveLocation({
            level: entry.level,
            physicalAddress: entry.physicalAddress,
            hosted: entry.hosted,
            storeTitle: getStoreTitle(centre, entry),
          }),
          occurrences: getOccurrences(true, entry, centre.timeZone),
          entryAnalyticsMetadata: entry.analyticsMetadata,
        }
      }

      if (
        ['Half Half (With Image)', 'Half Half (Text Only)'].includes(
          collectionProps.displayMode
        )
      ) {
        return {
          ...collectionProps,
          entries: entries.map(e => ({
            body: e.shortDescription,
            id: e.sys.id,
            imageUrl: getImageFromEntry(e, STANDARD).src,
            title: e.title,
            link: getEntryLink(centre.slug, e, { supportsAbsolute: true }),
            topic: getSponsoredTopic(e),
            location: getDescriptiveLocation({
              level: e.level,
              physicalAddress: e.physicalAddress,
              hosted: e.hosted,
              storeTitle: getStoreTitle(centre, e),
            }),
            occurrences: getOccurrences(true, e, centre.timeZone),
            analyticsMetadata: e.analyticsMetadata,
          })),
        }
      }

      // NOTE: Not entirely sure if this component is still being used on
      // the Westfield site as of this comment writing, but leaving the code
      // here as-is for now in case removing it breaks something.
      if (['Accordion'].includes(collectionProps.displayMode)) {
        return {
          ...collectionProps,
          entries: entries.map(e => ({
            body: e.shortDescription,
            imageUrl: getImageFromEntry(e, MASTER).src,
            id: e.sys.id,
            title: e.title,
            link: getEntryLink(centre.slug, e),
            topic: getSponsoredTopic(e),
            location: getDescriptiveLocation({
              level: e.level,
              physicalAddress: e.physicalAddress,
              hosted: e.hosted,
              storeTitle: getStoreTitle(centre, e),
            }),
            occurrences: getOccurrences(true, e, centre.timeZone),
            analyticsMetadata: e.analyticsMetadata,
          })),
        }
      }

      if (['Deck'].includes(collectionProps.displayMode)) {
        return {
          ...collectionProps,
          title: moduleData.title,
          centre,
          entries: entries.map(e => ({
            body: e.title,
            id: e.sys.id,
            link: e.url || getEntryLink(centre.slug, e),
            image: getImageFromEntry(e, SQUARE).src,
            title: getSponsoredTopic(e),
            location: getDescriptiveLocation({
              level: e.level,
              physicalAddress: e.physicalAddress,
              hosted: e.hosted,
              storeTitle: getStoreTitle(centre, e),
            }),
            occurrences: getOccurrences(true, e, centre.timeZone),
            analyticsMetadata: e.analyticsMetadata,
          })),
          callToActionLabel: moduleData.callToActionLabel,
          callToActionAddress: moduleData.callToActionAddress,
        }
      }

      if (['Featured Content'].includes(collectionProps.displayMode)) {
        return {
          ...collectionProps,
          title: moduleData.title,
          entries: entries.map(e => ({
            body: e.title,
            id: e.sys.id,
            cost: e.priceDescription,
            link: e.url || getEntryLink(centre.slug, e),
            image: getImageFromEntry(e, SQUARE).src,
            title: getSponsoredTopic(e),
            location: getDescriptiveLocation({
              level: e.level,
              physicalAddress: e.physicalAddress,
              hosted: e.hosted,
              storeTitle: getStoreTitle(centre, e),
            }),
            occurrences: getOccurrences(true, e, centre.timeZone),
            analyticsMetadata: e.analyticsMetadata,
          })),
        }
      }

      if (['WhatsHappeningSpotlight'].includes(collectionProps.displayMode)) {
        return {
          ...collectionProps,
          heading: moduleData.title,
          entries: entries.map(e => ({
            link: getEntryLink(centre.slug, e),
            desktopImageUrl: e.image.src,
            mobileImageUrl: e.image.src,
            title: e.heading,

            category: e.category,
            heading: e.heading,
            body: e.body,
            location: getDescriptiveLocation({
              level: e.level,
              physicalAddress: e.location,
              hosted: e.hosted,
              storeTitle: getStoreTitle(centre, e),
            }),
            occurrences: e.occurrences,
            analyticsMetadata: e.analyticsMetadata,
          })),
        }
      }

      if (['FilteredCardList'].includes(collectionProps.displayMode)) {
        return {
          ...collectionProps,
          ...moduleData,
        }
      }
    }
  }

  // If we get this far, we don't know how to handle this module. Sorry bout it!
  return null
}
